import { useState, useEffect, useCallback } from "react";
import { getLang, getLocale, getCurrencySymbol } from "../lang/useLanguage";
import { useTheme } from "../context/ThemeContext";
import PaginationBar from "../components/PaginationBar";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const pu = () => getLang().purchasing || {};
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_CFG = {
  pending:   { labelKey: "statusPending",   color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
  approved:  { labelKey: "statusApproved",  color: "#60a5fa", bg: "rgba(96,165,250,0.12)" },
  ordered:   { labelKey: "statusOrdered",   color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
  received:  { labelKey: "statusReceived",  color: "#00ff9d", bg: "rgba(0,255,157,0.12)" },
  checked:   { labelKey: "statusChecked",   color: "#34d399", bg: "rgba(52,211,153,0.12)" },
  rejected:  { labelKey: "statusRejected",  color: "#f87171", bg: "rgba(248,113,113,0.12)" },
  cancelled: { labelKey: "statusCancelled", color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
};

const PRIORITY_CFG = {
  low:    { labelKey: "priorityLow",    color: "#64748b" },
  normal: { labelKey: "priorityNormal", color: "#60a5fa" },
  high:   { labelKey: "priorityHigh",   color: "#fbbf24" },
  urgent: { labelKey: "priorityUrgent", color: "#f87171" },
};

const statusLabel = (status) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return pu()[cfg.labelKey] || status;
};

const priorityLabel = (priority) => {
  const cfg = PRIORITY_CFG[priority] || PRIORITY_CFG.normal;
  return pu()[cfg.labelKey] || priority;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {statusLabel(status)}
    </span>
  );
};

// ── YENİ TALEP FORMU ──────────────────────────────────────────────────────
const NewRequestModal = ({ onClose, onSaved, t, suppliers }) => {
  const [form, setForm] = useState({
    material_name: "", material_code: "", quantity_needed: "",
    quantity_available: "0", quantity_requested: "", unit: "adet",
    priority: "normal", supplier_id: "", supplier_name: "", unit_price: "", notes: "", work_order_no: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filteredSuppliers, setFilteredSuppliers] = useState(suppliers);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [stockMaterials, setStockMaterials] = useState([]);
  const [showMatDropdown, setShowMatDropdown] = useState(false);

  useEffect(() => { setFilteredSuppliers(suppliers); }, [suppliers]);
  useEffect(() => {
    fetch(`${API}/stock?materials_only=true`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(d => { const list = Array.isArray(d) ? d : (d.items || []); setStockMaterials(list); })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.material_name || !form.quantity_requested) {
      setError(pu().requiredMaterialQuantity); return;
    }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/purchase-requests`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          ...form,
          quantity_needed: parseFloat(form.quantity_needed) || 0,
          quantity_available: parseFloat(form.quantity_available) || 0,
          quantity_requested: parseFloat(form.quantity_requested) || 0,
          unit_price: parseFloat(form.unit_price) || 0,
          supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
          supplier_name: form.supplier_name || "",
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || pu().genericError);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const S = {
    input: { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" },
    label: { display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 },
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 580, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>🛒 {pu().newRequest}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={S.label}>{pu().materialName} *</label>
              <div style={{ position: "relative" }}>
                <input type="text" value={form.material_name} onChange={async e => {
                  const val = e.target.value;
                  setForm(p => ({ ...p, material_name: val, supplier_id: "", supplier_name: "" }));
                  if (val.length > 1) {
                    setLoadingSuppliers(true);
                    try {
                      const res = await fetch(`${API}/suppliers/by-material?material_name=${encodeURIComponent(val)}`, { headers: headers() });
                      if (res.ok) { const data = await res.json(); setFilteredSuppliers(Array.isArray(data) ? data : suppliers); }
                    } catch { setFilteredSuppliers(suppliers); }
                    setLoadingSuppliers(false);
                  } else { setFilteredSuppliers(suppliers); }
                }} onFocus={() => setShowMatDropdown(true)} onBlur={() => setTimeout(() => setShowMatDropdown(false), 150)} style={S.input} placeholder={pu().materialNamePlaceholder} autoComplete="off" />
                {showMatDropdown && form.material_name.length > 0 && stockMaterials.filter(m => (m.material_name || m.name) && (m.material_name || m.name).toLowerCase().includes(form.material_name.toLowerCase())).length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1e2130", border: "1px solid #334155", borderRadius: 8, zIndex: 999, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
                    {stockMaterials.filter(m => (m.material_name || m.name) && (m.material_name || m.name).toLowerCase().includes(form.material_name.toLowerCase())).slice(0, 8).map((m, i) => (
                      <div key={i} onMouseDown={e => {
                        e.preventDefault();
                        setForm(p => ({ ...p, material_name: m.material_name || m.name, quantity_available: String(m.current_quantity || m.current_stock || 0), unit: m.unit || p.unit }));
                        setShowMatDropdown(false);
                      }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, color: "#e2e8f0", borderBottom: "1px solid #334155", display: "flex", justifyContent: "space-between", background: "transparent" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#2d3748"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <span style={{ fontWeight: 600 }}>{m.material_name || m.name}</span>
                        <span style={{ color: "#94a3b8", fontSize: 11 }}>
  {pu().stockLabel}: {m.current_quantity || m.current_stock} {m.unit} · {m.unit_price} {getCurrencySymbol()}/{m.unit}
</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div><label style={S.label}>{pu().materialCode}</label><input type="text" value={form.material_code} onChange={e => setForm(p => ({ ...p, material_code: e.target.value }))} style={S.input} /></div>
            <div><label style={S.label}>{pu().workOrderNo}</label><input type="text" value={form.work_order_no} onChange={e => setForm(p => ({ ...p, work_order_no: e.target.value }))} style={S.input} placeholder={pu().workOrderPlaceholder} /></div>
            <div><label style={S.label}>{pu().neededQuantity}</label><input type="number" value={form.quantity_needed} onChange={e => setForm(p => ({ ...p, quantity_needed: e.target.value }))} style={S.input} min="0" /></div>
            <div><label style={S.label}>{pu().availableQuantity}</label><input type="number" value={form.quantity_available} onChange={e => setForm(p => ({ ...p, quantity_available: e.target.value }))} style={S.input} min="0" /></div>
            <div><label style={S.label}>{pu().requestQuantity} *</label><input type="number" value={form.quantity_requested} onChange={e => setForm(p => ({ ...p, quantity_requested: e.target.value }))} style={S.input} min="0" /></div>
            <div>
              <label style={S.label}>{pu().unit}</label>
              <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} style={S.input}>
                {["adet", "kg", "mt", "lt", "m2", "paket"].map(u => <option key={u} value={u}>{u.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>{pu().priority}</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} style={S.input}>
                <option value="low">{pu().priorityLow}</option>
                <option value="normal">{pu().priorityNormal}</option>
                <option value="high">{pu().priorityHigh}</option>
                <option value="urgent">{pu().priorityUrgent}</option>
              </select>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={S.label}>{pu().supplier}</label>
              <select value={form.supplier_id} onChange={e => {
                const sel = suppliers.find(s => String(s.id) === e.target.value);
                setForm(p => ({ ...p, supplier_id: e.target.value, supplier_name: sel ? (sel.name || sel.supplier_name || "") : "" }));
              }} style={S.input}>
                <option value="">{pu().selectSupplierOptional}</option>
                {loadingSuppliers
                  ? <option>{pu().loading}</option>
                  : filteredSuppliers.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.supplier_name}{s.order_count > 0 ? ` ★ (${s.order_count} ${pu().orderCount})` : ""}
                      {s.last_price > 0 ? ` · ${pu().lastPrice}: ${s.last_price} ${getCurrencySymbol()}` : ""}
                    </option>
                  ))
                }
              </select>
            </div>
            <div>
              <label style={S.label}>{pu().unitPrice} ({getCurrencySymbol()})</label>
              <input type="number" value={form.unit_price} onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))} style={S.input} min="0" step="0.01" placeholder="0.00" />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={S.label}>{pu().notes}</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} style={{ ...S.input, resize: "vertical" }} />
            </div>
          </div>
          {error && <div style={{ marginTop: 14, padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{pu().cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? pu().creatingRequest : `🛒 ${pu().createRequest}`}</button>
        </div>
      </div>
    </div>
  );
};

// ── HAMMADDE GİRİŞ KK MODAL (DEPOCU) ─────────────────────────────────────
const IncomingQCModal = ({ order, onClose, onSaved, t }) => {
  const [form, setForm] = useState({
    quantity_received: order.quantity_ordered || "",
    quantity_ok: "",
    quantity_broken: "0",
    quantity_damaged: "0",
    quantity_missing: "0",
    check_notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalChecked = (parseFloat(form.quantity_ok) || 0) +
    (parseFloat(form.quantity_broken) || 0) +
    (parseFloat(form.quantity_damaged) || 0);
  const qualityRate = totalChecked > 0 ? ((parseFloat(form.quantity_ok) || 0) / totalChecked * 100).toFixed(1) : 0;

  const handleSave = async () => {
    if (!form.quantity_received || !form.quantity_ok) {
      setError(pu().requiredReceivedGood); return;
    }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/incoming-quality`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          purchase_order_id: order.id,
          order_no: order.order_no,
          supplier_name: order.supplier_name,
          material_name: order.material_name,
          quantity_expected: parseFloat(order.quantity_ordered),
          quantity_received: parseFloat(form.quantity_received),
          quantity_ok: parseFloat(form.quantity_ok),
          quantity_broken: parseFloat(form.quantity_broken) || 0,
          quantity_damaged: parseFloat(form.quantity_damaged) || 0,
          quantity_missing: parseFloat(form.quantity_missing) || 0,
          check_notes: form.check_notes,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || pu().genericError);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const S = {
    input: { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" },
    label: { display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 },
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 600, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>🔍 {pu().incomingQcTitle}</div>
            <div style={{ fontSize: 13, color: t.text.muted, marginTop: 2 }}>{order.order_no} · {order.material_name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ padding: "14px 18px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 12, marginBottom: 20, fontSize: 13 }}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div><span style={{ color: t.text.muted }}>{pu().supplier}: </span><strong style={{ color: t.text.primary }}>{order.supplier_name}</strong></div>
              <div><span style={{ color: t.text.muted }}>{pu().orderQuantity}: </span><strong style={{ color: t.accent.blue }}>{order.quantity_ordered} {order.unit}</strong></div>
              <div><span style={{ color: t.text.muted }}>Beklenen Tarih: </span><strong style={{ color: t.text.primary }}>{fmt(order.expected_date)}</strong></div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={S.label}>{pu().receivedQuantity} *</label>
              <input type="number" value={form.quantity_received} onChange={e => setForm(p => ({ ...p, quantity_received: e.target.value }))} style={S.input} min="0" step="0.01" />
            </div>
            <div>
              <label style={{ ...S.label, color: "#00ff9d" }}>✅ {pu().goodQuantity} *</label>
              <input type="number" value={form.quantity_ok} onChange={e => setForm(p => ({ ...p, quantity_ok: e.target.value }))} style={{ ...S.input, borderColor: "#00ff9d44" }} min="0" step="0.01" />
            </div>
            <div>
              <label style={{ ...S.label, color: "#f87171" }}>❌ {pu().brokenQuantity}</label>
              <input type="number" value={form.quantity_broken} onChange={e => setForm(p => ({ ...p, quantity_broken: e.target.value }))} style={{ ...S.input, borderColor: "#f8717144" }} min="0" step="0.01" />
            </div>
            <div>
              <label style={{ ...S.label, color: "#fbbf24" }}>⚠️ {pu().damagedQuantity}</label>
              <input type="number" value={form.quantity_damaged} onChange={e => setForm(p => ({ ...p, quantity_damaged: e.target.value }))} style={{ ...S.input, borderColor: "#fbbf2444" }} min="0" step="0.01" />
            </div>
            <div>
              <label style={{ ...S.label, color: "#94a3b8" }}>📦 {pu().missingQuantity}</label>
              <input type="number" value={form.quantity_missing} onChange={e => setForm(p => ({ ...p, quantity_missing: e.target.value }))} style={{ ...S.input, borderColor: "#94a3b844" }} min="0" step="0.01" />
            </div>
          </div>

          {totalChecked > 0 && (
            <div style={{ marginTop: 16, padding: "14px 18px", background: parseFloat(qualityRate) >= 95 ? "rgba(0,255,157,0.08)" : parseFloat(qualityRate) >= 80 ? "rgba(251,191,36,0.08)" : "rgba(248,113,113,0.08)", border: `1px solid ${parseFloat(qualityRate) >= 95 ? "#00ff9d44" : parseFloat(qualityRate) >= 80 ? "#fbbf2444" : "#f8717144"}`, borderRadius: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, color: t.text.muted, marginBottom: 4 }}>{pu().qualityRate}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: parseFloat(qualityRate) >= 95 ? "#00ff9d" : parseFloat(qualityRate) >= 80 ? "#fbbf24" : "#f87171" }}>%{qualityRate}</div>
                </div>
                <div style={{ textAlign: "right", fontSize: 12, color: t.text.muted }}>
                  <div>Toplam kontrol: {totalChecked}</div>
                  <div>{pu().good}: {form.quantity_ok || 0}</div>
                  <div>{pu().defective}: {(parseFloat(form.quantity_broken) || 0) + (parseFloat(form.quantity_damaged) || 0)}</div>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label style={S.label}>{pu().checkNotes}</label>
            <textarea value={form.check_notes} onChange={e => setForm(p => ({ ...p, check_notes: e.target.value }))} rows={3} style={{ ...S.input, resize: "vertical" }} placeholder={pu().checkNotesPlaceholder} />
          </div>

          {error && <div style={{ marginTop: 14, padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>

        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{pu().cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#059669,#10b981)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? pu().creatingRequest : `✅ ${pu().statusChecked}`}</button>
        </div>
      </div>
    </div>
  );
};

// ── KK ONAY MODAL (SATIN ALMACI) ──────────────────────────────────────────
const QCApprovalModal = ({ iq, onClose, onSaved, t }) => {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApprove = async (approved) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/incoming-quality/${iq.id}/approve`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({ approved, notes }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || pu().genericError);
      onSaved();
    } catch {}
    setLoading(false);
  };

  const qRate = parseFloat(iq.quality_rate) || 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>🔍 {pu().qcApprovalTitle}</div>
            <div style={{ fontSize: 13, color: t.text.muted, marginTop: 2 }}>{iq.order_no} · {iq.material_name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase" }}>📋 {pu().warehouseQcResults}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                { label: pu().expectedQuantity, value: iq.quantity_expected, color: t.accent.blue },
                { label: pu().receivedQuantity, value: iq.quantity_received, color: t.text.primary },
                { label: pu().good, value: iq.quantity_ok, color: "#00ff9d" },
                { label: pu().broken, value: iq.quantity_broken, color: "#f87171" },
                { label: pu().damaged, value: iq.quantity_damaged, color: "#fbbf24" },
                { label: pu().missing, value: iq.quantity_missing, color: "#94a3b8" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: "center", padding: "10px", background: t.bg.tag, borderRadius: 10 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color }}>{value || 0}</div>
                  <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: "12px 16px", background: qRate >= 95 ? "rgba(0,255,157,0.08)" : qRate >= 80 ? "rgba(251,191,36,0.08)" : "rgba(248,113,113,0.08)", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: t.text.muted }}>{pu().qualityRate}</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: qRate >= 95 ? "#00ff9d" : qRate >= 80 ? "#fbbf24" : "#f87171" }}>%{qRate}</span>
            </div>

            {iq.check_notes && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: t.bg.tag, borderRadius: 8, fontSize: 13, color: t.text.secondary }}>
                📝 {iq.check_notes}
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: t.text.muted }}>
              {pu().checkedBy}: <strong>{iq.checked_by}</strong> · {fmtDT(iq.checked_at)}
            </div>
          </div>

          {qRate < 80 && (
            <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 10, fontSize: 13, color: t.accent.red, fontWeight: 600 }}>
              ⚠️ {pu().lowQualityWarning}
            </div>
          )}

          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{pu().approvalNotes}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              style={{ width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box", resize: "vertical" }}
              placeholder={pu().approvalNotesPlaceholder} />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => handleApprove(false)} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
              ❌ Reddet — Stoka Ekleme
            </button>
            <button onClick={() => handleApprove(true)} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
              ✅ {pu().approveAddStock} ({iq.quantity_ok})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── TALEP DETAY MODAL ─────────────────────────────────────────────────────
const RequestDetailModal = ({ req, onClose, onRefresh, t, suppliers, userRole }) => {
  const [status, setStatus] = useState(req.status);
  const [selectedSupplier, setSelectedSupplier] = useState(req.supplier_id || "");
  const [loading, setLoading] = useState(false);
  const canApprove = ["admin", "manager", "purchasing", "warehouse_chief"].includes(userRole);

  const handleStatusUpdate = async (newStatus) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/purchase-requests/${req.id}/status`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({ status: newStatus, supplier_id: selectedSupplier || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || pu().genericError);
        setLoading(false); return;
      }
      setStatus(newStatus); onRefresh();
    } catch (e) { alert(`${pu().connectionError}: ${e.message}`); }
    setLoading(false);
  };

  const S = { input: { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" } };

  const NEXT_ACTIONS = {
    pending: [
      { status: "approved", label: `✅ ${pu().approve}`, color: t.accent.green },
      { status: "cancelled", label: `❌ ${pu().cancel}`, color: t.accent.red }
    ],
    approved: [
      { status: "ordered", label: `🚚 ${pu().statusOrdered}`, color: "#a78bfa" }
    ],
    ordered: [
      { status: "received", label: `📦 ${pu().statusReceived}`, color: t.accent.green }
    ],
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 620, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary }}>{req.request_no}</div>
              <div style={{ fontSize: 13, color: t.text.muted }}>{req.material_name}</div>
            </div>
            <StatusBadge status={status} />
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase" }}>📋 {pu().requestDetails}</div>
            {[
              [pu().requestNo, req.request_no], [pu().material, req.material_name],
              [pu().materialCode, req.material_code || "—"], [pu().workOrder, req.work_order_no || "—"],
              [pu().neededQuantity, `${req.quantity_needed} ${req.unit}`],
              [pu().availableQuantity, `${req.quantity_available} ${req.unit}`],
              [pu().requestQuantity, `${req.quantity_requested} ${req.unit}`],
              [pu().priority, priorityLabel(req.priority)],
              [pu().requestedBy, req.requested_by], [pu().approvedBy, req.approved_by || "—"],
              [pu().date, fmtDT(req.created_at)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                <span style={{ color: t.text.label }}>{k}</span>
                <span style={{ color: t.text.primary, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>

          {canApprove && status !== "received" && status !== "cancelled" && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{pu().supplier}</label>
              <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} style={S.input}>
                <option value="">{pu().selectSupplier}</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name || s.supplier_name}</option>)}
              </select>
            </div>
          )}

          {req.notes && <div style={{ padding: "10px 14px", background: t.bg.tag, borderRadius: 8, fontSize: 13, color: t.text.secondary }}>📝 {req.notes}</div>}

          {canApprove && NEXT_ACTIONS[status] && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {NEXT_ACTIONS[status].map(action => (
                <button key={action.status} onClick={() => handleStatusUpdate(action.status)} disabled={loading} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: action.color, color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}>{action.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── ANA SAYFA ─────────────────────────────────────────────────────────────
export default function Purchasing() {
  const { t } = useTheme();
  const [activeTab, setActiveTab] = useState(localStorage.getItem("tab_purchasing") || "requests");
  const [requests, setRequests] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [incomingQC, setIncomingQC] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showIncomingQC, setShowIncomingQC] = useState(null);
  const [showQCApproval, setShowQCApproval] = useState(null);
  const [stats, setStats] = useState({});
  const userRole = localStorage.getItem("role") || "operator";

  const isWarehouse = ["admin", "manager", "warehouse", "warehouse_chief", "purchasing"].includes(userRole);
  const isBuyer = ["admin", "manager", "purchasing", "warehouse_chief"].includes(userRole);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/purchase-requests`, { headers: headers() }),
        fetch(`${API}/suppliers`, { headers: headers() }),
        fetch(`${API}/purchase-orders`, { headers: headers() }),
        fetch(`${API}/incoming-quality`, { headers: headers() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        const list = Array.isArray(data) ? data : [];
        setRequests(list);
        setStats({
          total: list.length,
          pending: list.filter(r => r.status === "pending").length,
          approved: list.filter(r => r.status === "approved").length,
          ordered: list.filter(r => r.status === "ordered").length,
          received: list.filter(r => r.status === "received").length,
          urgent: list.filter(r => r.priority === "urgent").length,
        });
      }
      if (r2.ok) { const data = await r2.json(); setSuppliers(Array.isArray(data) ? data : (data.suppliers || [])); }
      if (r3.ok) { const data = await r3.json(); setPurchaseOrders(Array.isArray(data) ? data : []); }
      if (r4.ok) { const data = await r4.json(); setIncomingQC(Array.isArray(data) ? data : []); }
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = requests.filter(r => {
    const matchSearch = !search || (r.material_name || "").toLowerCase().includes(search.toLowerCase()) || (r.request_no || "").toLowerCase().includes(search.toLowerCase()) || (r.work_order_no || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || r.status === filterStatus;
    const matchPriority = filterPriority === "all" || r.priority === filterPriority;
    return matchSearch && matchStatus && matchPriority;
  });

  const pagedRequests = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const pendingDelivery = purchaseOrders.filter(o => o.status === "ordered" && !o.iq_status);
  const pendingQCApproval = incomingQC.filter(iq => iq.status === "checked" && !iq.buyer_approved);

  const selectStyle = { padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer" };

  const TABS = [
    { id: "requests", label: `🛒 ${pu().tabRequests}` },
    { id: "incoming", label: `📦 ${pu().tabIncomingQc}${pendingDelivery.length > 0 ? ` (${pendingDelivery.length})` : ""}` },
    { id: "approval", label: `✅ ${pu().tabQcApproval}${pendingQCApproval.length > 0 ? ` (${pendingQCApproval.length})` : ""}` },
  ];

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0 }}>🛒 {pu().pageTitle}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {pu().pageSubtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
            {activeTab === "requests" && (
              <button onClick={() => setShowNew(true)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {pu().newRequestButton}</button>
            )}
          </div>
        </div>

        {pendingDelivery.length > 0 && isWarehouse && (
          <div style={{ padding: "12px 18px", background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.3)", borderRadius: 12, color: t.accent.blue, fontSize: 13, fontWeight: 600, marginBottom: 16, cursor: "pointer" }} onClick={() => { setActiveTab("incoming"); localStorage.setItem("tab_purchasing", "incoming"); }}>
            📦 {pu().deliveryWaitingMessage?.replace(
              "{count}",
              String(pendingDelivery.length)
            )}
          </div>
        )}
        {pendingQCApproval.length > 0 && isBuyer && (
          <div style={{ padding: "12px 18px", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 12, color: "#34d399", fontSize: 13, fontWeight: 600, marginBottom: 16, cursor: "pointer" }} onClick={() => { setActiveTab("approval"); localStorage.setItem("tab_purchasing", "approval"); }}>
            ✅ {pu().qcApprovalWaitingMessage?.replace(
              "{count}",
              String(pendingQCApproval.length)
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: pu().totalRequests, value: stats.total || 0, color: t.accent.blue, icon: "📊" },
            { label: pu().statusPending, value: stats.pending || 0, color: t.accent.yellow, icon: "⏳" },
            { label: pu().ordered, value: stats.ordered || 0, color: "#a78bfa", icon: "🚚" },
            { label: pu().received, value: stats.received || 0, color: t.accent.green, icon: "📦" },
            { key: "qcWaiting", label: pu().qcWaiting, value: pendingQCApproval.length, color: "#34d399", icon: "🔍" },
            { key: "urgent", label: pu().priorityUrgent, value: stats.urgent || 0, color: t.accent.red, icon: "🚨" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 120, background: t.bg.card, border: `1px solid ${s.value > 0 && (s.key === "urgent" || s.key === "qcWaiting") ? s.color + "44" : t.border.card}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: `1px solid ${t.border.divider}` }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); localStorage.setItem("tab_purchasing", tab.id); }} style={{ padding: "10px 20px", background: "none", border: "none", cursor: "pointer", color: activeTab === tab.id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${activeTab === tab.id ? t.accent.blue : "transparent"}`, transition: "all 0.2s" }}>{tab.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{pu().loading}</span>
          </div>
        ) : (
          <>
            {activeTab === "requests" && (
              <>
                {stats.urgent > 0 && <div style={{ padding: "12px 18px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 12, color: t.accent.red, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>🚨 {pu().urgentRequestsWaiting.replace("{count}", stats.urgent)}</div>}
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  <input placeholder={`🔍 ${pu().searchPlaceholder}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
                    <option value="all">{pu().allStatuses}</option>
                    {Object.entries(STATUS_CFG).map(([k]) => <option key={k} value={k}>{statusLabel(k)}</option>)}
                  </select>
                  <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={selectStyle}>
                    <option value="all">{pu().allPriorities}</option>
                    {Object.entries(PRIORITY_CFG).map(([k]) => <option key={k} value={k}>{priorityLabel(k)}</option>)}
                  </select>
                </div>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[
                          pu().requestNo,
                          pu().material,
                          pu().workOrder,
                          pu().needed,
                          pu().request,
                          pu().unit,
                          pu().priority,
                          pu().requestedBy,
                          pu().date,
                          pu().statusLabel,
                          pu().action
                        ].map(h => (
                          <th key={h} style={{ padding: "12px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={11} style={{ textAlign: "center", color: t.text.placeholder, padding: 60 }}>
                          {requests.length === 0 ? pu().noRequests : pu().noMatchingRequests}
                        </td></tr>
                      ) : pagedRequests.map((r, i) => {
                        const pCfg = PRIORITY_CFG[r.priority] || PRIORITY_CFG.normal;
                        return (
                          <tr key={r.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: r.priority === "urgent" ? `${t.accent.red}08` : i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                            <td style={{ padding: "11px 12px", fontWeight: 700, color: t.accent.blue, fontSize: 12 }}>{r.request_no}</td>
                            <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{r.material_name}</td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: "#a78bfa" }}>{r.work_order_no || "—"}</td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.accent.blue }}>{r.quantity_needed}</td>
                            <td style={{ padding: "11px 12px", fontSize: 13, fontWeight: 700, color: t.accent.red }}>{r.quantity_requested}</td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.muted }}>{r.unit}</td>
                            <td style={{ padding: "11px 12px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: pCfg.color, background: `${pCfg.color}18`, padding: "2px 8px", borderRadius: 20 }}>{priorityLabel(r.priority)}</span>
                            </td>
                            <td style={{ padding: "11px 12px", fontSize: 12, color: t.text.muted }}>{r.requested_by || "—"}</td>
                            <td style={{ padding: "11px 12px", fontSize: 11, color: t.text.muted }}>{fmt(r.created_at)}</td>
                            <td style={{ padding: "11px 12px" }}><StatusBadge status={r.status} /></td>
                            <td style={{ padding: "11px 12px" }}>
                              <button onClick={() => setSelected(r)} style={{ padding: "5px 12px", borderRadius: 7, cursor: "pointer", fontSize: 11, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, fontWeight: 600 }}>{pu().action}</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === "incoming" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {pendingDelivery.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: t.text.placeholder, fontSize: 14 }}>
                    ✅ {pu().noPendingDelivery}
                  </div>
                ) : pendingDelivery.map(order => (
                  <div key={order.id} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: t.shadow.card }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: t.accent.blue }}>{order.order_no}</span>
                        <StatusBadge status={order.status} />
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: t.text.primary, marginBottom: 4 }}>{order.material_name}</div>
                      <div style={{ display: "flex", gap: 20, fontSize: 12, color: t.text.muted }}>
                        <span>🏭 {order.supplier_name}</span>
                        <span>📦 {order.quantity_ordered} {order.unit}</span>
                        <span>📅 Beklenen: {fmt(order.expected_date)}</span>
                      </div>
                    </div>
                    {isWarehouse && (
                      <button onClick={() => setShowIncomingQC(order)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                        🔍 {pu().doIncomingQc}
                      </button>
                    )}
                  </div>
                ))}

                {incomingQC.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>{pu().qcHistory}</div>
                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: t.bg.theadRow }}>
                            {[
                              pu().orderNo,
                              pu().material,
                              pu().supplier,
                              pu().expected,
                              pu().delivered,
                              pu().good,
                              pu().broken,
                              pu().damaged,
                              pu().qualityPercent,
                              pu().statusLabel,
                              pu().checkedBy
                            ].map(h => (
                              <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {incomingQC.map((iq, i) => {
                            const qRate = parseFloat(iq.quality_rate) || 0;
                            return (
                              <tr key={iq.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                                <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: t.accent.blue }}>{iq.order_no}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: t.text.primary }}>{iq.material_name}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{iq.supplier_name}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12 }}>{iq.quantity_expected}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12 }}>{iq.quantity_received}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: "#00ff9d", fontWeight: 700 }}>{iq.quantity_ok}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.accent.red }}>{iq.quantity_broken}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.accent.yellow }}>{iq.quantity_damaged}</td>
                                <td style={{ padding: "10px 12px", fontWeight: 800, color: qRate >= 95 ? "#00ff9d" : qRate >= 80 ? "#fbbf24" : "#f87171" }}>%{qRate}</td>
                                <td style={{ padding: "10px 12px" }}><StatusBadge status={iq.buyer_approved ? "approved" : iq.status} /></td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{iq.checked_by}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "approval" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {pendingQCApproval.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: t.text.placeholder, fontSize: 14 }}>
                    ✅ {pu().noPendingQcApproval}
                  </div>
                ) : pendingQCApproval.map(iq => {
                  const qRate = parseFloat(iq.quality_rate) || 0;
                  return (
                    <div key={iq.id} style={{ background: t.bg.card, border: `1px solid ${qRate < 80 ? t.accent.red + "44" : t.border.card}`, borderRadius: 14, padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: t.shadow.card }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: t.accent.blue }}>{iq.order_no}</span>
                          <span style={{ fontSize: 20, fontWeight: 800, color: qRate >= 95 ? "#00ff9d" : qRate >= 80 ? "#fbbf24" : "#f87171" }}>%{qRate}</span>
                          {qRate < 80 && <span style={{ fontSize: 11, fontWeight: 700, color: t.accent.red, background: t.accent.redBg, padding: "2px 8px", borderRadius: 20 }}>⚠️ {pu().lowQuality}</span>}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: t.text.primary, marginBottom: 4 }}>{iq.material_name}</div>
                        <div style={{ display: "flex", gap: 20, fontSize: 12, color: t.text.muted }}>
                          <span>🏭 {iq.supplier_name}</span>
                          <span style={{ color: "#00ff9d" }}>✅ {pu().good}: {iq.quantity_ok}</span>
                          <span style={{ color: t.accent.red }}>❌ {pu().broken}: {iq.quantity_broken}</span>
                          <span style={{ color: t.accent.yellow }}>⚠️ {pu().damaged}: {iq.quantity_damaged}</span>
                          <span>👤 {iq.checked_by}</span>
                        </div>
                      </div>
                      {isBuyer && (
                        <button onClick={() => setShowQCApproval(iq)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", marginLeft: 16 }}>
                          🔍 {pu().reviewApprove}
                        </button>
                      )}
                    </div>
                  );
                })}

                {incomingQC.filter(iq => iq.buyer_approved).length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>✅ {pu().approvedStocked}</div>
                    {incomingQC.filter(iq => iq.buyer_approved).map(iq => (
                      <div key={iq.id} style={{ background: "rgba(0,255,157,0.04)", border: "1px solid rgba(0,255,157,0.2)", borderRadius: 12, padding: "14px 20px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <span style={{ fontWeight: 700, color: t.accent.blue, marginRight: 12 }}>{iq.order_no}</span>
                          <span style={{ fontWeight: 600, color: t.text.primary }}>{iq.material_name}</span>
                        </div>
                        <div style={{ display: "flex", gap: 16, fontSize: 12, color: t.text.muted }}>
                          <span style={{ color: "#00ff9d", fontWeight: 700 }}>{pu().addedToStock.replace("{count}", iq.quantity_ok)}</span>
                          <span>{pu().approvedBy}: {iq.buyer_approved_by}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {!loading && activeTab === "requests" && filtered.length > 0 && (
        <PaginationBar
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRecords={filtered.length}
          label="talep"
          t={t}
        />
      )}

      {showNew && <NewRequestModal t={t} suppliers={suppliers} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); fetchData(); }} />}
      {selected && <RequestDetailModal req={selected} t={t} suppliers={suppliers} userRole={userRole} onClose={() => setSelected(null)} onRefresh={() => { fetchData(); setSelected(null); }} />}
      {showIncomingQC && <IncomingQCModal order={showIncomingQC} t={t} onClose={() => setShowIncomingQC(null)} onSaved={() => { setShowIncomingQC(null); fetchData(); }} />}
      {showQCApproval && <QCApprovalModal iq={showQCApproval} t={t} onClose={() => setShowQCApproval(null)} onSaved={() => { setShowQCApproval(null); fetchData(); }} />}
    </div>
  );
}
